"use client";
import { api } from "@/lib/api/api";
import { IResourcePermission } from "@gettactic/api";
import { CrudModal } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  permissions: IResourcePermission[];
  resourceIds: string[];
};

export const ToggleIsApprovablePermission = ({
  isOpen,
  onOpenChange,
  permissions,
  resourceIds
}: Props) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);

  const onToggleSubmit = async () => {
    setLoading(true);

    let toggled = 0;
    let error = 0;
    for (const permission of permissions) {
      const res =
        await api.client.organizations.resourceScheduledPermissionsUpdate(
          {
            ...permission,
            since: permission.since ?? undefined,
            is_approvable: !permission.is_approvable
          },
          permission.resource_id,
          permission.id,
          permission.team?.id,
          permission.user?.id
        );
      if (res.error) {
        error++;
      } else {
        toggled++;
      }
    }
    setLoading(false);
    queryClient.invalidateQueries({
      queryKey: ["organizations.resourceScheduledPermissionsAll"]
    });
    if (error > 0) {
      toast.error("Sorry we couldn't change all the permission");
    } else {
      toast.success(
        toggled === 1 ? "Assignment updated" : "Group of assignments updated"
      );
      onOpenChange(false);
    }
  };

  return (
    <CrudModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Requires Approval"
      onSubmit={onToggleSubmit}
      submitLabel="Update"
      cancelLabel="Cancel"
      submitProps={{
        disabled: loading,
        isLoading: loading
      }}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
          <svg
            className="h-6 w-6 text-red-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-base text-neutral-800">
          Are you sure you want to toggle "Requires Approval" for
          <strong className="font-bold">
            {permissions.length === 1
              ? " this assignment"
              : " this group of assignments"}
          </strong>
          ?
        </p>
      </div>
    </CrudModal>
  );
};
